<template>
  <table>
    <thead>
      <tr>
        <th v-if="checkboxColumn" width="auto">
          <input class="tree-grid__checkbox tree-grid__checkbox--check-all" type="checkbox" :checked="isAllChecked" :indeterminate.prop="isAllIndeterminate" @change="onCheckAll" />
        </th>
        <th v-for="(column, columnIndex) in columns" :key="columnIndex" :width="column.width || 'auto'">
          <slot :name="column.prop + '--header-cell'" v-bind:column="column">
            {{ column.label ? column.label : '' }}
          </slot>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr class="ischild">
        <td :colspan="columns.length">
          <v-progress-linear :active="progressLoading" color="primary accent-2" indeterminate></v-progress-linear>
        </td>
      </tr>
      <template v-show="cloneTree.length > 0" v-if="isTestShow">
        <TreeGridRow v-for="(row, index) in cloneTree" :key="index" :columns="columns" :row="row" :children-property="childrenProperty" :row-class="rowClass" :checkbox-column="checkboxColumn" @row-checked="onRowChecked" @row-expanded="onRowExpanded" @testrow-expanded="testonRowExpanded" ref="rowalertDump">
          <template v-for="slot in rowCellsSlotNames" :slot="slot" slot-scope="scope">
            <slot :name="slot" v-bind="scope" />
          </template>
        </TreeGridRow>
      </template>
      <!-- <template v-else>
        <tr class="tree-grid__empty-body-row">
          <td :colspan="columns.length">
            {{ emptyText }}
          </td>
        </tr>
      </template> -->
    </tbody>
  </table>
</template>

<script>
import { set, get } from 'lodash';
import TreeGridRow from './TreeGridRow.vue';

export default {
  name: 'TreeGridTable',
  components: { TreeGridRow },
  props: {
    columns: {
      type: Array,
      required: true,
    },
    tree: {
      type: Array,
      required: true,
    },
    searchQuery: {
      type: String,
      default: '',
    },
    childrenProperty: {
      type: String,
      default: 'children',
    },
    emptyText: {
      type: String,
      default: 'No data',
    },
    rowClass: {
      type: Function,
      default: () => '',
    },
    checkboxColumn: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      checked_rows: [],
      cloneTree: [],
      progressLoading: true,
      isTestShow: true,
    };
  },
  computed: {
    vclasses() {
      return this.classes;
    },
    enabledRootRows() {
      return this.cloneTree.filter(({ _isDisabled }) => !_isDisabled);
    },
    isAllChecked() {
      return this.enabledRootRows.every(({ _isChecked }) => _isChecked);
    },
    isAllIndeterminate() {
      return !this.isAllChecked && (this.enabledRootRows.some(({ _isChecked }) => _isChecked) || this.enabledRootRows.some(({ _isIndeterminate }) => _isIndeterminate));
    },
    rowCellsSlotNames() {
      return Object.keys(this.$scopedSlots).filter(slotKey => slotKey.startsWith('cell.'));
    },
  },
  watch: {
    tree: {
      handler(val) {
        this.buildTree(val);
        this.progressLoading = false;
      },
      deep: true,
      immediate: true,
    },
    cloneTree: {
      handler() {
        this.isTestShow = true;
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    onRowChecked(row, isChecked) {
      this.toggleCheckRow(row, isChecked).then(() => {
        this.getAllCheckedRows().then(checkedRows => {
          this.$emit('check', checkedRows);
        });
      });
    },
    testonRowExpanded(row, isExpanded) {
      this.$emit('preExpand', row);
      this.toggleExpandRow(row, isExpanded).then(() => {
        this.$emit('add', row);
      });
    },
    onRowExpanded(row, isExpanded) {
      this.toggleExpandRow(row, isExpanded).then(() => {
        this.$emit('expand', row);
      });
    },
    onCheckAll(event) {
      return Promise.all(this.cloneTree.map(row => this.toggleCheckRow(row, event.target.checked))).then(() => {
        this.getAllCheckedRows().then(checkedRows => {
          this.$emit('check', checkedRows);
        });
      });
    },
    getAllCheckedRows(parent = null) {
      let checkedRows = [];
      return new Promise(resolve => {
        if (parent === null) {
          Promise.all(this.cloneTree.map(child => this.getAllCheckedRows(child))).then(checkedChildrenRows => {
            checkedChildrenRows
              .filter(rows => rows.length > 0)
              .forEach(rows => {
                checkedRows = checkedRows.concat(rows);
              });
            resolve(checkedRows);
          });
        } else if (parent._isChecked || parent._isIndeterminate) {
          if (parent._isChecked) {
            checkedRows.push(parent);
          }
          if (parent._childrenLength > 0) {
            Promise.all(parent[this.childrenProperty].map(child => this.getAllCheckedRows(child))).then(checkedChildrenRows => {
              checkedChildrenRows
                .filter(rows => rows.length > 0)
                .forEach(rows => {
                  checkedRows = checkedRows.concat(rows);
                });
              resolve(checkedRows);
            });
          } else {
            resolve(checkedRows);
          }
        } else {
          resolve([]);
        }
      });
    },
    buildTree(tree) {
      this.cloneTree = tree.map((row, index) => this.buildTreeItem(row, index));
    },
    buildTreeItem(row, index, parent = null, level = 1) {
      this.isTestShow = false;
      const treeItem = {
        _isChecked: false,
        _isIndeterminate: false,
        _isDisabled: false,
        _isHidden: false,
        _isFolded: false,
        _isExpanded: false,
        _childrenLength: 0,
        ...row,
        _level: level,
        _path: parent !== null ? [parent._path, this.childrenProperty, index].join('.') : index.toString(),
        _index: index,
      };

      if (parent !== null) {
        if (parent._isHidden) {
          treeItem._isHidden = true;
        }

        if (!parent._isExpanded) {
          treeItem._isFolded = true;
        }
      }

      if (Object.prototype.hasOwnProperty.call(treeItem, this.childrenProperty)) {
        treeItem[this.childrenProperty] = treeItem[this.childrenProperty].map((child, idx) => this.buildTreeItem(child, idx, treeItem, level + 1));
        treeItem._childrenLength = treeItem[this.childrenProperty].length;
        treeItem._isIndeterminate = this.isIndeterminate(treeItem);
      }

      treeItem._isChecked = this.isChecked(treeItem, parent);

      return treeItem;
    },
    getParent(row) {
      return this.getValue(row._path.split('.').slice(0, -2));
    },
    getChildren(row) {
      let children = [];
      const concat = row => {
        const ch = row[this.childrenProperty] ? row[this.childrenProperty] : [];
        children = children.concat(ch);
        ch.forEach(item => concat(item));
      };
      concat(row);
      return children;
    },
    toggleCheckRow(row, isChecked, updateParent = true) {
      return new Promise(resolve => {
        if (row._isDisabled) {
          resolve();
        } else {
          if (row._childrenLength > 0) {
            Promise.all(row[this.childrenProperty].map(child => this.toggleCheckRow(child, isChecked, false))).then(() => {
              this.setValue(row._path, '_isChecked', isChecked);
            });
          } else {
            this.setValue(row._path, '_isChecked', isChecked);
          }
          if (updateParent) {
            let parent = this.getParent(row);
            while (parent) {
              const isParentIndeterinate = this.isIndeterminate(parent);
              const isParentChecked = this.isChecked(parent);

              if (isParentIndeterinate) {
                this.setValue(parent._path, '_isChecked', false);
                this.setValue(parent._path, '_isIndeterminate', true);
              } else {
                this.setValue(parent._path, '_isChecked', isParentChecked);
                this.setValue(parent._path, '_isIndeterminate', false);
              }

              parent = this.getParent(parent);
            }
          }

          resolve();
        }
      });
    },
    setValue(path, key, value) {
      set(this.cloneTree, [path, key].join('.'), value);
    },
    getValue(path, defaultValue = null) {
      return get(this.cloneTree, path, defaultValue);
    },
    toggleExpandRow(row, isExpanded) {
      return new Promise(resolve => {
        this.setValue(row._path, '_isExpanded', isExpanded);
        const children = this.getChildren(row);
        if (isExpanded) {
          [row].concat(children).forEach(child => {
            if (child._isExpanded && child[this.childrenProperty]) {
              child[this.childrenProperty].forEach(item => this.setValue(item._path, '_isFolded', false));
            }
          });
        } else {
          children.forEach(child => this.setValue(child._path, '_isFolded', true));
        }
        resolve();
      });
    },
    isChecked(row, parent) {
      if (row._childrenLength === 0) {
        if (parent) {
          return parent._isChecked || row._isChecked;
        }

        return row._isChecked;
      }

      return row[this.childrenProperty].every(child => child._isChecked);
    },
    isIndeterminate(row) {
      if (row._childrenLength === 0) {
        return false;
      }
      return !row[this.childrenProperty].every(child => child._isChecked) && row[this.childrenProperty].some(child => child._isChecked || child._isIndeterminate);
    },
  },
};
</script>

<style lang="scss" scoped>
.tree-grid__empty-body-row {
  text-align: center;
}

/* .ischild {
  background-color: none !important;
} */

.theme--light.v-data-table tbody tr:nth-child(odd) {
  background-color: rgba(0, 0, 0, 0.03);
}

/* .theme--dark.v-data-table tbody tr:nth-child(1) {
  background-color: none !important;
} */
/* .theme--dark.v-data-table tbody tr:nth-child(odd) {
  background-color: rgba(255, 255, 255, 0.05);
} */
</style>
